import { CONFIRM_DELETE } from '@opengov/stories-shared/constants';
import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';

import { IStoryListMessages } from '../../../../types';
import { useDeleteStories } from '../hooks';

import { styles } from './BulkDeleteStoryModal.styles';

export interface IDeleteStoryModalProps extends RouteComponentProps {
  messages: IStoryListMessages;
  selectedStoryIds: string[];
  publicationId?: string;
  entityId: number;
  onDeleteSuccess?(): void;
  onClose?(): void;
}

const displayTitle = (selectedStoryIds: string[], messages: IStoryListMessages): string => {
  const storyCount = selectedStoryIds && selectedStoryIds.length;
  if (storyCount === 1) {
    return messages.deleteStory;
  } else if (storyCount > 1) {
    return messages.deleteStories({ value: storyCount });
  }
  return ``;
};

export const BulkDeleteStoryModal: React.FC<IDeleteStoryModalProps> = props => {
  const { selectedStoryIds, messages, publicationId, entityId, onDeleteSuccess } = props;
  const [inputTextValue, setInputTextValue] = useState<string>('');

  const deleteStoriesMutation = useDeleteStories({
    title: messages.somethingWentWrongError,
    body: messages.storiesFailedToDelete
  });

  const onConfirmDeleteStory = () => {
    deleteStoriesMutation.mutate(
      { storyIds: selectedStoryIds, publicationId: publicationId ?? '', entityId },
      {
        onSuccess: () => {
          onDeleteSuccess?.();
          props.onClose?.();
        }
      }
    );
  };

  return (
    <Dialog
      open={true}
      onClose={props.onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: styles.dialogPaper }}
      data-test="dialog-bulk-delete-story"
      aria-labelledby="bulk-delete-story-dialog-title"
    >
      <DialogTitle id="bulk-delete-story-dialog-title" sx={styles.dialogTitle}>
        {displayTitle(selectedStoryIds, messages)}
        <IconButton aria-label="close" onClick={props.onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          label={messages.deleteConfirmation}
          sx={styles.textField}
          value={inputTextValue}
          onChange={(e) => setInputTextValue(e.target.value)}
          inputProps={{ 'data-test': 'delete-confirmation-input', className: 'no-format' }}
        />
        <Box>
          <Typography variant="body2">{messages.deleteStoryDescription}</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={props.onClose}
          data-test="cancel-delete-button"
        >
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          color="error"
          size="small"
          disabled={inputTextValue !== CONFIRM_DELETE}
          onClick={onConfirmDeleteStory}
          data-test="confirm-delete-button"
        >
          {messages.delete}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
